import {
  Box,
  Button,
  Collapse,
  Flex,
  HStack,
  IconButton,
  Text,
  VStack
} from '@chakra-ui/react'
import { MagicEditIcon } from '@icons'
import AddIcon from '@material-ui/icons/Add'
import RemoveIcon from '@material-ui/icons/Remove'
import WarningIcon from '@material-ui/icons/Warning'
import React, { useState } from 'react'
import ClientSpecificTag from './client-specific-tag'

interface AppliedMemoryProps {
  memories?: string[]
  invalidMemories?: string[]
  onManageClick?: () => void
  isDisabled: boolean
  noteTypeLabel?: string
}

const STORAGE_KEY = 'appliedMemoryCollapsed'

const AppliedMemory: React.FC<AppliedMemoryProps> = ({
  memories = [],
  invalidMemories = [],
  onManageClick,
  isDisabled,
  noteTypeLabel
}) => {
  const [isCollapsed, setIsCollapsed] = useState(() => {
    return !!localStorage.getItem(STORAGE_KEY) && invalidMemories.length === 0
  })

  const toggleCollapsed = () => {
    setIsCollapsed(prev => {
      const newValue = !prev
      if (newValue) {
        localStorage.setItem(STORAGE_KEY, 'true')
      } else {
        localStorage.removeItem(STORAGE_KEY)
      }
      return newValue
    })
  }

  if (isDisabled) {
    return (
      <Box
        border="1px solid"
        borderColor="pale_gray"
        borderRadius="8px"
        py="12px"
        px="16px"
        width="100%"
        bg="rgba(249, 249, 249, 1)"
        mb="24px"
      >
        <Text color="dark_gray" textAlign="center">
          Memory is not supported for this note type yet and was not applied
        </Text>
      </Box>
    )
  }

  return (
    <Box
      border="1px solid"
      borderColor="pale_gray"
      bg="#F9F9F9"
      borderRadius="8px"
      overflow="hidden"
      width="100%"
      mb="24px"
    >
      <Box>
        <Flex
          pt="12px"
          pb="8px"
          px="20px"
          justify="space-between"
          align="center"
          cursor="pointer"
          onClick={toggleCollapsed}
          gap="12px"
          minH="48px"
        >
          <HStack spacing="8px" flex="1">
            <MagicEditIcon />
            <Text fontSize="16px" fontWeight="bold">
              Memory applied to this {noteTypeLabel} note ({memories.length})
            </Text>
          </HStack>
          <IconButton
            aria-label={isCollapsed ? 'Expand' : 'Collapse'}
            icon={
              isCollapsed ? (
                <AddIcon style={{ fontSize: 20 }} />
              ) : (
                <RemoveIcon style={{ fontSize: 20 }} />
              )
            }
            variant="ghost"
            p="0"
            m="0"
            minW="auto"
            color="black"
            _hover={{ bg: 'transparent' }}
            _focus={{ outline: 'none' }}
          />
        </Flex>

        <Collapse in={!isCollapsed}>
          <Box px="20px" pb="16px">
            <VStack align="start" spacing="0">
              {memories.map(memory => (
                <Box
                  key={memory}
                  display="flex"
                  justifyContent="flex-start"
                  alignItems="flex-start"
                  width="100%"
                >
                  <Box
                    w="16px"
                    h="16px"
                    mr="8px"
                    mt="2px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    fontSize="16px"
                  >
                    {invalidMemories.includes(memory) ? (
                      <WarningIcon
                        style={{
                          fontSize: '16px',
                          color: '#F8D557'
                        }}
                      />
                    ) : (
                      <Text
                        fontSize="16px"
                        color="dark_gray"
                        textAlign="center"
                      >
                        •
                      </Text>
                    )}
                  </Box>
                  <Text fontSize="14px" color="dark_gray">
                    {memory}
                    {invalidMemories.includes(memory) && <ClientSpecificTag />}
                  </Text>
                </Box>
              ))}
            </VStack>
            <Box mt="8px">
              <Text fontSize="14px" color="dark_gray">
                <Text fontSize="14px" as="span" fontWeight="bold">
                  Important:
                </Text>{' '}
                Saved memories directly affect how{' '}
                <Text fontSize="14px" as="span" fontWeight="bold">
                  all {noteTypeLabel} notes
                </Text>{' '}
                are generated. If any instruction is no longer relevant, please
                update it in{' '}
                <Button
                  variant="link"
                  textDecoration="none"
                  p="0"
                  m="0"
                  fontSize="14px"
                  fontWeight="normal"
                  onClick={e => {
                    e.stopPropagation()
                    onManageClick?.()
                  }}
                  _focus={{ outline: 'none' }}
                  _hover={{ textDecoration: 'underline' }}
                  isDisabled={!onManageClick}
                >
                  Manage Memory
                </Button>
                .
              </Text>
            </Box>
          </Box>
        </Collapse>
      </Box>
    </Box>
  )
}

export default AppliedMemory
